import type { ReactElement } from 'react';
import { Box, SimpleGrid } from '@mantine/core';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import type { DashboardTileConfig } from '@/shared/assist/DashboardContract';
import type { AnalyticsField, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import type { DashboardLayoutDef, SlotSpan } from './AIExplorerLayouts';
import AIExplorerTile from './AIExplorerTile';
import AIExplorerTilePlaceholder from './AIExplorerTilePlaceholder';

interface AIExplorerLayoutGridProps {
    layout: DashboardLayoutDef;
    /** The slots this composition will fill, in order — placeholders show for the ones not yet arrived. */
    slotIds: string[];
    /** slotId → its streamed tile config (absent until the tile frame arrives). */
    tiles: Record<string, DashboardTileConfig>;
    pageFilters: AnalyticsFilter[];
    basePath: string;
    onDrill: (field: AnalyticsField, value: string) => void;
}

/**
 * Renders a chosen layout's semantic slots into the house responsive grid. A
 * slot with a streamed tile renders the tile (which wipes in); a slot still
 * awaiting its config renders a shimmering placeholder. Spans are clamped to the
 * active breakpoint's column count so a wide slot never forces an extra column
 * on a narrow screen.
 */
export default function AIExplorerLayoutGrid({ layout, slotIds, tiles, pageFilters, basePath, onDrill }: AIExplorerLayoutGridProps): ReactElement {
    const cols = useActiveColumns(layout);
    const slots = layout.slots.filter((slot) => slotIds.includes(slot.id));

    return (
        <SimpleGrid cols={layout.columns} spacing="lg" style={{ alignItems: 'stretch', gridAutoFlow: 'dense' }}>
            {slots.map((slot) => {
                const tile = tiles[slot.id];
                return (
                    <Box key={slot.id} style={{ gridColumn: gridColumnFor(slot.span, cols) }}>
                        {tile !== undefined ?
                            <AIExplorerTile config={tile} pageFilters={pageFilters} onDrill={onDrill} basePath={basePath} accent={slot.accent} emphasis={slot.emphasis} />
                        :   <AIExplorerTilePlaceholder label={slot.role} minHeight={slot.emphasis === 'hero' ? 220 : undefined} />}
                    </Box>
                );
            })}
        </SimpleGrid>
    );
}

function gridColumnFor(span: SlotSpan, cols: number): string | undefined {
    if (span === 'full') return '1 / -1';
    const effective = Math.min(span, cols);
    return effective > 1 ? `span ${effective}` : undefined;
}

function useActiveColumns(layout: DashboardLayoutDef): number {
    const upSm = useBreakpointUp('sm');
    const upMd = useBreakpointUp('md');
    const upLg = useBreakpointUp('lg');
    const upXl = useBreakpointUp('xl');
    const { base, sm, md, lg, xl } = layout.columns;
    if (upXl && xl !== undefined) return xl;
    if (upLg && lg !== undefined) return lg;
    if (upMd && md !== undefined) return md;
    if (upSm && sm !== undefined) return sm;
    return base;
}
